import { type DeliveryAddress } from '@koala/sdk/v4';
import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { transparentize } from 'polished';
import { SavedAddresses } from './saved-addresses';
import {
  type GoogleAddress,
  GoogleAutocomplete,
  type MissingStreetNumberDetails,
} from '@/components/autocomplete';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Box } from '@/components/ui';
import { useLocation } from '@/features/locations/service';
import { useSavedAddresses } from '@/features/loyalty/addresses';
import {
  addressCheckDeliveryCoverage,
  addressSetTimeWantedFields,
} from '@/features/menu-availability/address.service';
import { useSelector, useDispatch } from '@/redux';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import conveyanceModeActions from '@/redux/conveyanceMode/actions';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import globalActions from '@/redux/global/actions';
import { SPACING_SCALE } from '@/constants/styles';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';
import * as ErrorReporter from '@/utils/errorReporter';
import { ERROR_MESSAGES } from '@/constants/events';
import { coverageErrorEventName, coverageErrorMessage } from '@/constants/coverageErrorCopy';
import { prepareErrorMessage } from '@/utils/global';
import { addressValidation } from '@/components/fulfillmentManager/deliveryManager/deliveryForm/validation';
import { CheckMarkCircle } from '@/components/uielements/icon/check-mark-circle';
import { Input } from '@/components/uielements/forms/input';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { isValidZip } from '@/utils/locations';

// How the incomplete-address message names each addressValidation field (TEAM-15164).
const MISSING_FIELD_LABELS: Record<string, string> = {
  street_address: 'street address',
  city: 'city',
  state: 'state',
  zip_code: 'ZIP code',
};

/** ['city'] → "city"; ['city', 'zip_code'] → "city and ZIP code"; three → "a, b and c". */
const describeMissingFields = (fields: string[]) => {
  const labels = fields.map((field) => MISSING_FIELD_LABELS[field] ?? field);
  return labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    : labels[0];
};

interface DeliveryDetailsProps {
  onSwitchToPickup?: () => void;
}

export function DeliveryDetails({ onSwitchToPickup }: DeliveryDetailsProps) {
  const { data: loggedInUser } = useSelector((state) => state.app.me);
  const [error, setError] = useState(false);
  // The addressValidation fields the last pick was missing, and that pick, so a ZIP-only gap can be
  // filled in on the page instead of re-searching (TEAM-15164). The pick is kept without its
  // time-wanted fields: the diner can change the time while the ZIP prompt is open.
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [pendingAddress, setPendingAddress] = useState<GoogleAddress | null>(null);
  const [zipSubmitting, setZipSubmitting] = useState(false);
  // Bumped by every coverage check and every new pick, so a check that resolves late can't commit
  // its address or show its message over a newer one (TEAM-15164).
  const coverageCheck = useRef(0);
  // Which refusal message the house-number guard shows. `check_failed` means Google itself failed,
  // not the address: an `unconfirmed` pick whose recovery errored (`recovery: 'error'`), or a pick
  // whose place-details lookup failed (TEAM-15253).
  const [streetNumberError, setStreetNumberError] = useState<
    MissingStreetNumberDetails['reason'] | 'check_failed' | null
  >(null);
  const [editing, setEditing] = useState(false);
  const [addressJustConfirmed, setAddressJustConfirmed] = useState(false);
  const { data } = useSavedAddresses(Boolean(loggedInUser?.email));
  const { location, time_wanted, address } = useSelector(selectConveyance);
  const { data: storeLocation } = useLocation(location);
  const { data: locationDetails, isSuccess: locationSuccess } = useLocation(location);
  const dispatch = useDispatch();
  const savedAddress = Boolean(data?.data.length);
  const clearMissingFields = () => {
    setMissingFields([]);
    setPendingAddress(null);
  };
  let streetAddress = address?.street_address;
  streetAddress =
    streetAddress && address?.street_address_2
      ? `${streetAddress} ${address.street_address_2}`
      : streetAddress;

  // Set delivery address only once when component mounts if we're in delivery mode with an existing address
  const hasSetInitialDeliveryAddress = useRef(false);
  useEffect(() => {
    if (hasSetInitialDeliveryAddress.current) return;
    if (address?.street_address && address?.city && locationDetails?.data) {
      hasSetInitialDeliveryAddress.current = true;
      const fullAddress = {
        ...address,
        ...addressSetTimeWantedFields(time_wanted),
      };
      dispatch(conveyanceModeActions.setDeliveryAddress(fullAddress, locationDetails.data));
    }
  }, []);

  useEffect(() => {
    if (address?.street_address && address?.city && editing) {
      setEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.city, address?.street_address]);

  if (!locationSuccess) {
    return null;
  }

  if (address?.street_address && !editing) {
    return (
      <AddressDetails>
        <Address>
          <h2>Delivering to</h2>
          <p>
            {streetAddress}, {address.city}
          </p>
        </Address>

        <EditAddress
          id="edit-delivery-address-button"
          onClick={() => {
            setEditing(true);
            setAddressJustConfirmed(false);
            // Re-opening the editor must not greet the diner with a message from an earlier pick.
            setError(false);
            clearMissingFields();
            setStreetNumberError(null);
          }}
        >
          Change delivery address
        </EditAddress>

        {addressJustConfirmed && (
          <StyledSuccessMessage>
            <StyledCheckMark title="Address confirmed" />
            <span>Address confirmed &mdash; your order will be delivered here</span>
          </StyledSuccessMessage>
        )}
      </AddressDetails>
    );
  }

  // Offered on every refusal that leaves the diner without a deliverable address (TEAM-15253).
  const switchToPickupOption =
    storeLocation?.data?.supports_pick_up && onSwitchToPickup ? (
      <>
        , or{' '}
        <StyledSwitchToPickupLink onClick={onSwitchToPickup}>
          switch to pickup instead
        </StyledSwitchToPickupLink>
      </>
    ) : null;

  // Shared by a Google pick and a ZIP the diner typed in (TEAM-15164): the backend coverage check is
  // the authority on whether the address can be delivered to. Resolves to false when a newer pick
  // made the answer stale, so the caller leaves that pick's in-flight state alone.
  const checkDeliveryCoverage = async (fullAddress: DeliveryAddress): Promise<boolean> => {
    const check = ++coverageCheck.current;

    try {
      const response = await addressCheckDeliveryCoverage(locationDetails.data.id, fullAddress);
      if (check !== coverageCheck.current) {
        return false;
      }

      clearMissingFields();
      if (response.can_deliver) {
        setError(false);
        setAddressJustConfirmed(true);
        dispatch(conveyanceModeActions.setDeliveryAddress(fullAddress, locationDetails.data));
      } else {
        setError(true);
        dispatch(conveyanceModeActions.setConveyanceLoading(false));
        ErrorReporter.captureException(new Error('Can not deliver to this address'), 'info', {
          address: fullAddress,
          locationId: locationDetails.data.id,
          eventName: ERROR_MESSAGES.CHECK_DELIVERY_COVERAGE_ERROR,
          details: response.message,
        });
      }
    } catch (error) {
      if (check !== coverageCheck.current) {
        return false;
      }

      // A failed check and an unconfirmable address are different things
      // and must not read alike: on a 502 nothing is known about the
      // address, so the copy cannot imply anything is wrong with it. A ZIP
      // prompt stays up with the typed ZIP so the diner can retry it.
      dispatch(globalActions.displayErrorToast(coverageErrorMessage(error), true));
      dispatch(conveyanceModeActions.setConveyanceLoading(false));
      const errorResponse = await prepareErrorMessage(null, error);
      ErrorReporter.captureException(error, 'error', {
        eventName: coverageErrorEventName(error),
        details: errorResponse,
      });
    }
    return true;
  };

  // TEAM-15164: neither Google nor the geocode recovery had a ZIP for the pick, so the diner typed
  // one. It isn't cross-checked against the city/state; the coverage check gets the full address.
  const submitMissingZip = async (zipCode: string) => {
    if (!pendingAddress) {
      return;
    }

    const fullAddress: DeliveryAddress = {
      ...pendingAddress,
      zip_code: zipCode,
      ...addressSetTimeWantedFields(time_wanted),
    };
    ErrorReporter.captureException(new Error('Delivery address ZIP entered by diner'), 'info', {
      address: fullAddress,
      locationId: locationDetails.data.id,
    });
    // Same in-flight state a pick has while its coverage check runs (the widget's onChange).
    dispatch(conveyanceModeActions.setConveyanceLoading(true));
    setZipSubmitting(true);
    // A stale answer must not re-enable Continue while a newer ZIP is being checked.
    if (await checkDeliveryCoverage(fullAddress)) {
      setZipSubmitting(false);
    }
  };

  const zipOnlyMissing = missingFields.length === 1 && missingFields[0] === 'zip_code';

  return (
    <>
      <GoogleAutocomplete
        requireStreetNumber
        onMissingStreetNumber={(address, details) => {
          // TEAM-15047: Google resolved the pick to a route with no house number. Catch it up
          // front with a specific field-level message (mirrors the missingFields path) instead of
          // committing an incomplete address that fails later at the delivery-quote step. Log the
          // resolved partial address so the Sentry signal keeps parity with the ZIP breadcrumb.
          // TEAM-15253: when the diner did type a number that couldn't be recovered, the message
          // must not ask them for one.
          setError(false);
          clearMissingFields();
          setAddressJustConfirmed(false);
          setStreetNumberError(
            details.reason === 'unconfirmed' && details.recovery === 'error'
              ? 'check_failed'
              : details.reason,
          );
          dispatch(conveyanceModeActions.setConveyanceLoading(false));
          ErrorReporter.captureException(
            new Error('Delivery address missing a street number'),
            'info',
            { address, locationId: locationDetails.data.id, ...details },
          );
        }}
        onStreetNumberRecovered={(recoveredAddress, { partialAddress, suggestion }) => {
          // TEAM-15253: counts the road-level picks whose typed house number was recovered; the
          // recovered address then goes through onUpdate like any other pick.
          ErrorReporter.captureException(
            new Error('Delivery address house number recovered'),
            'info',
            {
              address: partialAddress,
              recoveredAddress,
              suggestion,
              locationId: locationDetails.data.id,
            },
          );
        }}
        onPostalCodeRecovered={(recoveredAddress, { partialAddress, suggestion }) => {
          // TEAM-15164: counts the numbered picks whose missing ZIP was recovered; the recovered
          // address then goes through onUpdate like any other pick.
          ErrorReporter.captureException(new Error('Delivery address ZIP recovered'), 'info', {
            address: partialAddress,
            recoveredAddress,
            suggestion,
            locationId: locationDetails.data.id,
          });
        }}
        onSelectionAbandoned={(reason) => {
          // TEAM-15253: a pick that will never finish must not leave the conveyance loading flag set,
          // or isConveyanceInFlight keeps the Delivery / Pickup buttons disabled. On unmount this only
          // dispatches (no local state to update).
          dispatch(conveyanceModeActions.setConveyanceLoading(false));
          if (reason === 'details_failed') {
            setStreetNumberError('check_failed');
            ErrorReporter.captureException(
              new Error('Delivery address details lookup failed'),
              'warning',
              { locationId: locationDetails.data.id },
            );
          }
        }}
        onChange={() => {
          // A new pick starts clean: no message from the previous one lingers while it resolves.
          setError(false);
          clearMissingFields();
          setStreetNumberError(null);
          coverageCheck.current += 1;
          setZipSubmitting(false);
          dispatch(conveyanceModeActions.setConveyanceLoading(true));
        }}
        onUpdate={async (address) => {
          setStreetNumberError(null);
          const fullAddress: DeliveryAddress = {
            ...address,
            ...addressSetTimeWantedFields(time_wanted),
          };

          // Don't commit an incomplete Google selection. resolveCity and
          // resolvePostalCode can each legitimately return '' (and state may be
          // absent) when Google omits a component; addressValidation checks
          // street/city/state/zip against the same rules the other autocomplete
          // path uses, so we catch the whole class of late MalformedPayload
          // failures up front — not just #/customer/zip (TEAM-14096). Keep an
          // 'info' breadcrumb so we retain the signal that quantified the bug.
          // TEAM-15164: the message name is kept for Sentry continuity even though
          // it covers any missing field; the `missing_fields` tag says which.
          const missing = Object.keys(addressValidation(fullAddress));
          if (missing.length > 0) {
            setError(false);
            setAddressJustConfirmed(false);
            setMissingFields(missing);
            setPendingAddress(address);
            dispatch(conveyanceModeActions.setConveyanceLoading(false));
            ErrorReporter.captureException(
              new Error('Delivery address missing a valid ZIP'),
              'info',
              {
                address: fullAddress,
                locationId: locationDetails.data.id,
              },
              { missing_fields: missing.join(',') },
            );
            return;
          }

          await checkDeliveryCoverage(fullAddress);
        }}
      />

      {!error && !missingFields.length && !streetNumberError && !savedAddress && (
        <StyledWarningMessageContainer>
          <StyledWarningExclamationCircle title="Warning: No delivery address" />

          <StyledWarningMessage>
            <b>A delivery address is required</b> &mdash; please enter one to continue
            {storeLocation?.data?.supports_pick_up && onSwitchToPickup && (
              <>
                , or{' '}
                <StyledSwitchToPickupLink onClick={onSwitchToPickup}>
                  Switch to pickup instead
                </StyledSwitchToPickupLink>
              </>
            )}
          </StyledWarningMessage>
        </StyledWarningMessageContainer>
      )}

      {!error && !missingFields.length && !streetNumberError && !savedAddress && (
        <StyledAddShippingDetails>
          <StyledExclamationCircle title="Exclamation Circle" />

          <StyledAddShippingDetailsLabel>
            Add building, suite, floor, or apartment details in <b>Delivery Instructions</b> at
            checkout
          </StyledAddShippingDetailsLabel>
        </StyledAddShippingDetails>
      )}

      {/* Only the ZIP is missing — let the diner type it in instead of re-searching (TEAM-15164) */}
      {zipOnlyMissing && <MissingZipForm submitting={zipSubmitting} onSubmit={submitMissingZip} />}

      {/* Any other gap from autocomplete — names only the fields that are missing (TEAM-15164) */}
      {missingFields.length > 0 && !zipOnlyMissing && (
        <StyledErrorMessageContainer>
          <StyledErrorExclamationCircle title="Address details needed" />

          <StyledErrorMessage>
            <b>We couldn&apos;t read the {describeMissingFields(missingFields)} for that address</b>
            <span>Please choose a more specific address.</span>
          </StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      {/* Missing house number from autocomplete — actionable field-level message (TEAM-15047) */}
      {streetNumberError === 'no_house_number' && (
        <StyledErrorMessageContainer>
          <StyledErrorExclamationCircle title="Street number needed" />

          <StyledErrorMessage>
            <b>We couldn&apos;t find a street number for that address</b>
            <span>Please choose an address that includes a house/street number.</span>
          </StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      {/* Typed house number that Google could not confirm — never asks for a number (TEAM-15253).
          Copy confirmed by product (Kelcie Geark, 2026-09-30). */}
      {streetNumberError === 'unconfirmed' && (
        <StyledErrorMessageContainer>
          <StyledErrorExclamationCircle title="Address not confirmed" />

          <StyledErrorMessage>
            <b>We couldn&apos;t confirm that address</b>
            <span>
              Check the house number, or re-enter the address
              {switchToPickupOption}
            </span>
          </StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      {/* Google's geocoder itself failed: the address may be fine, so don't point at it (TEAM-15253) */}
      {streetNumberError === 'check_failed' && (
        <StyledErrorMessageContainer>
          <StyledErrorExclamationCircle title="Address check failed" />

          <StyledErrorMessage>
            <b>We couldn&apos;t check that address right now</b>
            <span>
              Please try again, or choose a nearby address
              {switchToPickupOption}
            </span>
          </StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      {/* Out-of-zone delivery rejection — prominent recovery message */}
      {error && (
        <StyledErrorMessageContainer>
          <StyledErrorExclamationCircle title="Delivery not available" />

          <StyledErrorMessage>
            <b>
              <StringAccessor accessor="locations.delivery_search_no_results_text" />
            </b>
            <span>
              Please try a different address
              {switchToPickupOption}
            </span>
          </StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
      {loggedInUser?.email ? (
        <Box
          css={{
            marginTop: '1rem',
          }}
        >
          <SavedAddresses
            value={address}
            onUpdate={(address) => {
              // A saved address is a newer pick too: a coverage check still in flight must not
              // override it, and no ZIP prompt from an earlier pick stays up (TEAM-15164).
              coverageCheck.current += 1;
              clearMissingFields();
              setZipSubmitting(false);
              const fullAddress: DeliveryAddress = {
                ...address,
                ...addressSetTimeWantedFields(time_wanted),
              };

              dispatch(
                conveyanceModeActions.setDeliveryAddress(fullAddress, locationDetails?.data),
              );
            }}
          />
        </Box>
      ) : null}
    </>
  );
}

interface MissingZipFormProps {
  submitting: boolean;
  onSubmit: (zipCode: string) => void;
}

/** Asks for the ZIP Google didn't return; only a valid US ZIP or Canadian postal code is submitted. */
function MissingZipForm({ submitting, onSubmit }: MissingZipFormProps) {
  const [zipCode, setZipCode] = useState('');
  const [invalid, setInvalid] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // The prompt appears after a combobox pick with nothing announcing it, so move focus to the one
  // thing the diner has to do next (screen-reader users otherwise never hear about it).
  useEffect(() => {
    input.current?.focus();
  }, []);

  return (
    <StyledErrorMessageContainer>
      <StyledErrorExclamationCircle title="ZIP code needed" />

      <StyledErrorMessage>
        <b>Add the ZIP code for that address</b>
        <span>
          Google didn&apos;t return a ZIP or postal code for this address. Enter it to continue.
        </span>

        <StyledZipForm
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = zipCode.trim();
            if (submitting) {
              return;
            }
            if (!isValidZip(trimmed)) {
              setInvalid(true);
              return;
            }
            onSubmit(trimmed);
          }}
        >
          <Input
            ref={input}
            id="delivery-zip-code"
            label="ZIP or postal code"
            autoComplete="postal-code"
            value={zipCode}
            aria-invalid={invalid}
            aria-describedby={invalid ? 'delivery-zip-code-error' : undefined}
            onChange={(event) => {
              setZipCode(event.target.value);
              setInvalid(false);
            }}
          />
          <StyledPrimaryButton
            data-css-override="StyledPrimaryButton"
            type="submit"
            disabled={submitting}
          >
            Continue
          </StyledPrimaryButton>
        </StyledZipForm>

        {invalid && (
          <StyledZipError id="delivery-zip-code-error" role="alert">
            Enter a 5-digit ZIP or a Canadian postal code.
          </StyledZipError>
        )}
      </StyledErrorMessage>
    </StyledErrorMessageContainer>
  );
}

const AddressDetails = styled.div({});

const Address = styled.address(({ theme }) => ({
  fontStyle: 'normal',

  'h2, p': {
    font: 'inherit',
    margin: 0,
    padding: 0,
  },

  h2: {
    opacity: 0.6,
    ...getFontStyles(
      theme.header.font,
      ['font_size', 'font_style', 'font_family', 'font_weight', 'line_height'],
      {
        font_size: (value) => {
          if (typeof value === 'number') {
            // shrink the size to 87.5% of the primary font size
            // but ensure a minimum of .875rem (14pt)
            return `max(0.875rem, ${value * 0.875}px)`;
          }

          return value;
        },
      },
    ),
    ...getFontStyles(theme.modals.font, ['color']),
  },

  p: {
    ...getFontStyles(theme.header.font, [
      'font_size',
      'font_style',
      'font_family',
      'font_weight',
      'line_height',
    ]),
    ...getFontStyles(theme.modals.font, ['color']),
  },
}));

const EditAddress = styled.button(({ theme }) => ({
  all: 'unset',
  outline: 'revert',
  display: 'block',
  color: theme.global.primary_active_color,
  fontWeight: 700,
  margin: '5px 0 0',
  ...getFontStyles(theme.header.font, ['font_family', 'font_size', 'font_style'], {
    font_size: (value) => {
      if (typeof value === 'number') {
        // shrink the size to 87.5% of the primary font size
        // but ensure a minimum of .875rem (14pt)
        return `max(0.875rem, ${value * 0.875}px)`;
      }

      return value;
    },
  }),

  '&:hover': {
    textDecoration: 'underline',
  },
}));

const StyledAddShippingDetails = styled.div`
  border: solid 1px ${({ theme }) => theme.global.primary_border_color};
  border-radius: ${SPACING_SCALE.THREEXS}px;
  padding: ${SPACING_SCALE.THREEXS}px;
  display: flex;
  gap: ${SPACING_SCALE.THREEXS}px;
  align-items: center;
  margin: 0.75rem 0 0;
`;

const StyledExclamationCircle = styled(ExclamationCircle)`
  color: ${({ theme }) => theme.global.primary_border_color};
  width: 24px;
  height: 24px;
`;

const StyledAddShippingDetailsLabel = styled.span`
  font-size: 13px;
  line-height: 18px;
`;

const StyledWarningMessageContainer = styled.div(({ theme }) => ({
  border: `1px solid ${theme.global.primary_active_color}`,
  backgroundColor: transparentize(0.95, theme.global.primary_active_color),
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderRadius: '8px',
  padding: '8px',
  marginBottom: '16px',
  marginTop: '10px',
}));

const StyledWarningMessage = styled.span(({ theme }) => ({
  fontSize: '13px',
  ...getFontStyles(theme.modals.font, ['color']),
}));

export const StyledWarningExclamationCircle = styled(ExclamationCircle)(({ theme }) => ({
  color: theme.global.primary_active_color,
  width: '24px',
  height: '24px',
}));

const StyledErrorMessageContainer = styled.div(({ theme }) => ({
  border: `1px solid ${theme.global.error_color}`,
  backgroundColor: transparentize(0.95, theme.global.error_color),
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  borderRadius: '8px',
  padding: '8px',
  marginTop: '10px',
}));

const StyledErrorMessage = styled.div(({ theme }) => ({
  fontSize: '13px',
  lineHeight: '18px',
  ...getFontStyles(theme.modals.font, ['color']),

  '& > b': {
    display: 'block',
    marginBottom: '4px',
  },
}));

const StyledErrorExclamationCircle = styled(ExclamationCircle)(({ theme }) => ({
  color: theme.global.error_color,
  width: '24px',
  height: '24px',
  flexShrink: 0,
  marginTop: '2px',
}));

// The input's label takes the first row; the input and Continue share the second.
const StyledZipForm = styled.form({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: '4px 8px',
  marginTop: '8px',

  '& > label': {
    gridColumn: '1 / -1',
  },
});

const StyledZipError = styled.span(({ theme }) => ({
  display: 'block',
  marginTop: '4px',
  color: theme.global.error_color,
}));

const StyledSwitchToPickupLink = styled.button(({ theme }) => ({
  all: 'unset',
  cursor: 'pointer',
  color: theme.global.primary_active_color,
  fontWeight: 600,
  textDecoration: 'underline',
}));

const StyledSuccessMessage = styled.div(({ theme }) => ({
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  ...getFontStyles(theme.modals.font, ['color']),
  borderRadius: '8px',
  border: `solid 1px ${theme.global.success_color}`,
  backgroundColor: transparentize(0.9, theme.global.success_color),
  fontSize: '13px',
  lineHeight: '18px',
  marginTop: '1rem',
}));

const StyledCheckMark = styled(CheckMarkCircle)(({ theme }) => ({
  width: '20px',
  height: '20px',
  flexShrink: 0,
  color: theme.global.success_color,
}));
